import { Component } from '@theme/component';

/**
 * A pill-style tab control for the header's "team" mega menu: shows one
 * team grid at a time, switched by clicking the tab bar above it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tab
 * @property {HTMLElement[]} panel
 *
 * @extends {Component<Refs>}
 */
class HeaderTeamMenu extends Component {
  requiredRefs = ['tab', 'panel'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #reflowFrame = null;

  connectedCallback() {
    super.connectedCallback();

    // header-menu.js snapshots this menu's rendered height on hover/focus to
    // drive the open animation. Anything that changes the visible panel's
    // size shortly after that - a webfont swapping in and rewrapping team
    // names, images settling, deferred hydration - leaves that snapshot too
    // short, clipping later rows until the menu is closed and reopened.
    // `.menu-list__submenu` is only visually hidden (visibility/clip-path),
    // not display:none, so it stays laid out and this observer keeps firing
    // for real size changes even while closed - reflowIfOpen no-ops then.
    this.#resizeObserver = new ResizeObserver(() => this.#scheduleReflow());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    if (this.#reflowFrame) cancelAnimationFrame(this.#reflowFrame);
  }

  /**
   * Coalesces bursts of resize notifications (e.g. several images settling
   * in the same frame) into a single reflow on the next frame.
   */
  #scheduleReflow() {
    if (this.#reflowFrame) return;
    this.#reflowFrame = requestAnimationFrame(() => {
      this.#reflowFrame = null;
      this.#reflowIfOpen();
    });
  }

  /**
   * Re-dispatches pointerenter on the ancestor nav item (the same
   * Safari hit-test workaround header-menu.js itself uses) so it re-runs its
   * height measurement, but only while this menu is actually open.
   */
  #reflowIfOpen() {
    const listItem = this.closest('.menu-list__list-item');
    const menuitem = listItem?.querySelector('[ref="menuitem"]');
    if (!listItem || menuitem?.getAttribute('aria-expanded') !== 'true') return;

    listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
  }

  /**
   * Activates the tab/panel at the given index.
   *
   * @param {{ index: number }} data
   */
  selectTab({ index }) {
    const { tab: tabs, panel: panels } = this.refs;

    tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('header-team-menu__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    // On narrower screens the tab bar scrolls horizontally instead of
    // wrapping, so the selected tab may be partially offscreen - center it
    // so the strip stays discoverable as you click through it.
    tabs[index]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

    panels.forEach((panel, i) => {
      panel.style.display = i === index ? 'block' : 'none';
    });
  }

  /**
   * Activates the clicked tab.
   *
   * @param {Event} event
   */
  onTabClick(event) {
    const { tab: tabs } = this.refs;
    const index = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (index === -1) return;

    this.selectTab({ index });
  }

  /**
   * Arrow-key navigation between tabs, per the ARIA tabs pattern.
   *
   * @param {KeyboardEvent} event
   */
  onKeyDown(event) {
    const { key } = event;
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(key)) return;

    const { tab: tabs } = this.refs;
    const current = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (current === -1) return;

    event.preventDefault();

    let next = current;
    if (key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (key === 'Home') next = 0;
    if (key === 'End') next = tabs.length - 1;

    this.selectTab({ index: next });
    tabs[next].focus();
  }
}

if (!customElements.get('header-team-menu-component')) {
  customElements.define('header-team-menu-component', HeaderTeamMenu);
}
